import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { apiClient } from '../services/api';
import { useAuth } from '../context/AuthContext';
import CreateProjectModal from '../components/CreateProjectModal';
import AppHeader from '../components/layout/AppHeader';
import ProjectsTour from '../components/onboarding/ProjectsTour';

interface Project {
  id?: string;
  _id?: string;
  name: string;
  description?: string;
  department?: string;
  status: string;
  members?: any[];
  dueDate?: string;
  createdAt?: string;
  metadata?: {
    department?: string;
  };
}

const Projects: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const fetchProjects = async () => {
    try {
      const data = await apiClient.getProjects();
      const safeProjects = Array.isArray(data)
        ? data
        : (data?.projects || data?.data || []);
      setProjects(safeProjects);
    } catch (err) {
      console.error('Failed to load projects:', err);
      // Fallback to empty list or handle error UI
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchProjects();
    } else {
      // Avoid indefinite loading state when not authenticated
      setLoading(false);
    }
  }, [user]);

  const filteredProjects = projects.filter((p: Project) => {
    const matchesFilter = filter === 'All' || p.status?.toLowerCase() === filter.toLowerCase();
    const matchesSearch = p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.description?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const handleStatusUpdate = async (projectId: string | undefined, newStatus: string) => {
    if (!projectId) return;
    try {
      await apiClient.updateProject(projectId, { status: newStatus });
      // Optimistic update or refetch
      setProjects((prev: Project[]) => prev.map((p: Project) =>
        (p.id === projectId || p._id === projectId) ? { ...p, status: newStatus } : p
      ));
    } catch (err) {
      console.error(`Failed to update project status to ${newStatus}:`, err);
      alert('Failed to update project status.');
    }
  };

  const handleDelete = async (projectId: string) => {
    if (!projectId) return;
    if (!window.confirm('Are you sure you want to delete this project? This action cannot be undone.')) return;

    try {
      await apiClient.deleteProject(projectId);
      setProjects((prev: Project[]) => prev.filter((p: Project) => p.id !== projectId && p._id !== projectId));
    } catch (err) {
      console.error('Failed to delete project:', err);
      alert('Failed to delete project. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-premium-wood-dark font-sans text-white overflow-x-hidden relative">
      {/* Wood Texture Background */}
      <div
        className="absolute inset-0 z-0 opacity-40 pointer-events-none mix-blend-overlay"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1543158143-4a1c0d0c3405?q=80&w=2670&auto=format&fit=crop')`,
          backgroundSize: 'cover',
          filter: 'contrast(1.2) brightness(0.8)'
        }}
      />
      <div className="absolute inset-0 z-0 bg-gradient-to-b from-black/60 via-premium-wood-dark/80 to-black/90 pointer-events-none" />

      {/* Tour Component */}
      <ProjectsTour />

      {/* Header */}
      <AppHeader />

      {/* Main Content */}
      <main className="relative z-10 container mx-auto px-4 pt-12 pb-24">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-7xl mx-auto">

          <div className="flex flex-col md:flex-row justify-between items-end mb-12 gap-6">
            <div>
              <h1 className="font-serif text-5xl md:text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-b from-premium-gold-light via-premium-gold to-premium-gold-dark drop-shadow-lg mb-2">
                Projects
              </h1>
              <p className="text-lg text-white/60">Manage your ongoing ventures and collaborations.</p>
            </div>
            <button
              id="projects-create-btn"
              onClick={() => setIsCreateModalOpen(true)}
              className="px-8 py-3 bg-gradient-to-r from-premium-gold-dark to-premium-gold-light text-premium-wood-dark font-bold rounded-lg hover:shadow-[0_0_20px_rgba(212,175,55,0.5)] transition-all transform hover:scale-105 active:scale-95"
            >
              + New Project
            </button>
          </div>

          {/* Filters & Search */}
          <div id="projects-filters" className="bg-black/20 border border-white/10 rounded-xl p-4 backdrop-blur-sm mb-8 flex flex-col md:flex-row gap-4 justify-between items-center">
            <div id="projects-filter-group" className="flex gap-2 overflow-x-auto pb-2 md:pb-0 w-full md:w-auto scrollbar-hide no-scrollbar -mx-2 px-2 sm:mx-0 sm:px-0">
              {['All', 'Active', 'Completed', 'On Hold'].map((f) => (
                <button
                  key={f}
                  id={`projects-filter-${f.replace(/\s+/g, '-').toLowerCase()}`}
                  onClick={() => setFilter(f)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0 ${filter === f
                    ? 'bg-premium-gold text-premium-wood-dark'
                    : 'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white'
                    }`}
                >
                  {f}
                </button>
              ))}
            </div>
            <div id="projects-search-container" className="relative w-full md:w-64">
              <input
                id="projects-search-input"
                type="text"
                placeholder="Search projects..."
                value={searchQuery}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 pl-10 text-white focus:outline-none focus:border-premium-gold/50 transition-colors"
              />
              <span className="absolute left-3 top-2.5 text-white/40">🔍</span>
            </div>
          </div>

          {/* Projects Grid */}
          <div id="projects-grid">
            {loading ? (
              <div className="text-center py-24">
                <div className="w-12 h-12 border-4 border-premium-gold border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <div className="text-white/60">Loading projects...</div>
              </div>
            ) : filteredProjects.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredProjects.map((project) => (
                  <motion.div
                    key={project.id || project._id}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="group bg-gradient-to-br from-white/10 to-transparent border border-white/10 rounded-xl p-6 backdrop-blur-sm hover:border-premium-gold/50 transition-all cursor-pointer relative overflow-visible"
                    onClick={() => navigate(`/projects/${project.id || project._id}`, { state: { department: project.metadata?.department || 'GEN' } })}
                  >
                    <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity text-premium-gold z-0 pointer-events-none">
                      <span className="text-8xl">★</span>
                    </div>

                    <div className="relative z-10">
                      <div className="flex justify-between items-start mb-4">
                        <span className={`px-2 py-1 rounded text-xs font-bold uppercase tracking-wider ${project.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                          project.status === 'on hold' ? 'bg-yellow-500/20 text-yellow-400' :
                            project.status === 'suspended' ? 'bg-red-500/20 text-red-400' :
                              'bg-premium-gold/20 text-premium-gold'
                          }`}>
                          {project.status || 'Active'}
                        </span>

                        {/* Actions Menu */}
                        <div className="relative" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                          <div className="group/menu relative">
                            <button className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center text-white/60 hover:text-white transition-colors">
                              ⋮
                            </button>
                            <div className="absolute right-0 top-full mt-2 w-40 bg-black/90 border border-white/10 rounded-lg shadow-xl py-2 opacity-0 group-hover/menu:opacity-100 invisible group-hover/menu:visible transition-all z-20 backdrop-blur-md flex flex-col">
                              <button className="w-full text-left px-4 py-2 text-sm text-white/80 hover:bg-white/10 hover:text-white">
                                Edit Project
                              </button>
                              {project.status !== 'suspended' && (
                                <button
                                  onClick={() => handleStatusUpdate(project.id || project._id, 'suspended')}
                                  className="w-full text-left px-4 py-2 text-sm text-yellow-400 hover:bg-white/10"
                                >
                                  Suspend
                                </button>
                              )}
                              {project.status !== 'completed' && (
                                <button
                                  onClick={() => handleStatusUpdate(project.id || project._id, 'completed')}
                                  className="w-full text-left px-4 py-2 text-sm text-green-400 hover:bg-white/10"
                                >
                                  Mark as Done
                                </button>
                              )}
                              <div className="h-[1px] bg-white/10 my-1"></div>
                              <button
                                onClick={() => handleDelete(project.id || project._id || '')}
                                className="w-full text-left px-4 py-2 text-sm text-red-500 hover:bg-white/10"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>

                      <h3 className="text-xl font-serif font-bold text-white mb-2 group-hover:text-premium-gold transition-colors truncate">
                        {project.name}
                      </h3>
                      <p className="text-sm text-white/60 mb-6 line-clamp-2 h-10">
                        {project.description || 'No description provided.'}
                      </p>

                      <div className="flex items-center justify-between border-t border-white/10 pt-4">
                        {/* ... existing footer code ... */}
                        <div className="flex -space-x-2">
                          {/* Mock avatars */}
                          {(project.members || []).slice(0, 3).map((m: any, i: number) => (
                            <div key={i} className="w-8 h-8 rounded-full bg-white/20 border border-black flex items-center justify-center text-xs">
                              {m.firstName?.[0] || 'U'}
                            </div>
                          ))}
                          {(project.members?.length || 0) > 3 && (
                            <div className="w-8 h-8 rounded-full bg-white/10 border border-black flex items-center justify-center text-xs">
                              +{(project.members?.length || 0) - 3}
                            </div>
                          )}
                          {(!project.members || project.members.length === 0) && (
                            <div className="text-xs text-white/40 italic">No members</div>
                          )}
                        </div>
                        <span className="text-xs text-white/40">
                          Due {new Date(project.dueDate || Date.now()).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-24 bg-white/5 border border-white/10 rounded-xl">
                <span className="text-4xl block mb-4">📂</span>
                <h3 className="text-xl font-bold text-white mb-2">No Projects Found</h3>
                <p className="text-white/60 max-w-md mx-auto mb-6">
                  You haven't started any projects yet. Create one to get started!
                </p>
                <button
                  onClick={() => setIsCreateModalOpen(true)}
                  className="px-6 py-2 border border-premium-gold text-premium-gold rounded-lg hover:bg-premium-gold hover:text-premium-wood-dark transition-colors"
                >
                  Create First Project
                </button>
              </div>
            )}
          </div>

        </motion.div>
      </main>

      {/* Create Project Modal */}
      <CreateProjectModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={fetchProjects}
      />
    </div>
  );
};

export default Projects;
